import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useStripe } from '@stripe/react-stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import './SubscriptionCheckout.css';

// Inner component that uses Stripe hooks (must be inside Elements provider)
const SubscriptionCheckoutInner = ({ user, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if Stripe is initialized
  useEffect(() => {
    if (!stripe) {
      setError('Stripe is not initialized. Please ensure REACT_APP_STRIPE_PUBLISHABLE_KEY is set in your .env file and restart the dev server.');
      setLoading(false);
    }
  }, [stripe]);

  useEffect(() => {
    const createCheckoutSession = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!user) {
          setError('User not authenticated. Please sign in.');
          setLoading(false);
          return;
        }

        // Call your backend API to create a checkout session
        let response;
        try {
          response = await fetch('/api/create-checkout-session', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId: user?.id,
              email: user?.primaryEmailAddress?.emailAddress,
            }),
          });
        } catch (networkErr) {
          // Network error (endpoint doesn't exist, CORS, etc.)
          console.error('Network error:', networkErr);
          throw new Error('Cannot connect to backend server. The /api/create-checkout-session endpoint is not available. Please set up your backend API. See STRIPE_BACKEND_SETUP.md for instructions.');
        }

        if (!response.ok) {
          let errorText = '';
          try {
            errorText = await response.text();
          } catch (e) {
            errorText = response.statusText;
          }
          console.error('API Error:', response.status, errorText);
          
          if (response.status === 404) {
            throw new Error('Backend API endpoint not found (404).\n\nPlease set up the backend server with the /api/create-checkout-session endpoint.\n\nSee STRIPE_BACKEND_SETUP.md for detailed instructions.');
          } else if (response.status === 405) {
            throw new Error('Method Not Allowed (405).\n\nThe /api/create-checkout-session endpoint exists but does not accept POST requests.\n\nIf using Vercel, make sure your API route in /api/create-checkout-session.js exports a POST handler.\n\nSee STRIPE_BACKEND_SETUP.md for Vercel setup instructions.');
          } else if (response.status >= 500) {
            throw new Error('Server error. Please try again later.');
          } else {
            throw new Error(`Failed to create checkout session (${response.status}): ${response.statusText}`);
          }
        }

        let data;
        try {
          data = await response.json();
        } catch (parseErr) {
          console.error('JSON parse error:', parseErr);
          throw new Error('Invalid response from server. Expected JSON but received invalid data.');
        }
        
        if (!data || !data.clientSecret) {
          throw new Error('Invalid response from server. Missing clientSecret field.');
        }
        
        setClientSecret(data.clientSecret);
      } catch (err) {
        console.error('Error creating checkout session:', err);
        const errorMessage = err.message || 'Failed to initialize checkout. Please try again.';
        setError(errorMessage);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      createCheckoutSession();
    } else {
      setLoading(false);
      setError('User not authenticated. Please sign in.');
    }
  }, [user]);

  const fetchClientSecret = async () => {
    // This function is called by EmbeddedCheckoutProvider
    // If we already have the client secret, return it
    if (clientSecret) {
      return clientSecret;
    }
    
    // Otherwise, fetch it again
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user?.id,
          email: user?.primaryEmailAddress?.emailAddress,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      return data.clientSecret;
    } catch (err) {
      console.error('Error fetching client secret:', err);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="subscription-checkout-container">
        <div className="subscription-checkout-loading">
          <div className="loading-spinner" />
          <p>Setting up secure checkout...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-checkout-container">
        <div className="subscription-checkout-error">
          <h3>⚠️ Checkout Error</h3>
          <p style={{ whiteSpace: 'pre-line', textAlign: 'left', marginBottom: '20px' }}>{error}</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => window.location.reload()} className="retry-button">
              Retry
            </button>
            {onCancel && (
              <button onClick={onCancel} className="cancel-button">
                Go Back
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!clientSecret && !loading) {
    return (
      <div className="subscription-checkout-container">
        <div className="subscription-checkout-error">
          <h3>⚠️ Checkout Unavailable</h3>
          <p>Unable to initialize checkout. Please ensure your backend API is configured correctly.</p>
          <p style={{ marginTop: '16px', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            Make sure you have set up the backend endpoint at <code style={{ background: 'rgba(255, 255, 255, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>/api/create-checkout-session</code>
          </p>
          {onCancel && (
            <button onClick={onCancel} className="cancel-button" style={{ marginTop: '20px' }}>
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-checkout-container">
      <div className="subscription-checkout-header">
        <img src="/logos/longLogo.png" alt="LinePicks" className="checkout-logo" />
        <h2>Subscribe to LinePicks</h2>
        <p>Get full access to all premium features</p>
        {onCancel && (
          <button
            onClick={onCancel}
            className="cancel-button"
            style={{ marginTop: '16px' }}
          >
            ← Back
          </button>
        )}
      </div>
      {stripe && clientSecret ? (
        <div style={{ width: '100%', maxWidth: '600px' }}>
          <EmbeddedCheckoutProvider
            stripe={stripe}
            options={{ fetchClientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      ) : !error ? (
        <div className="subscription-checkout-loading">
          <div className="loading-spinner" />
          <p>Initializing checkout...</p>
        </div>
      ) : null}
    </div>
  );
};

// Outer component that conditionally renders based on Stripe availability
const SubscriptionCheckout = ({ onSuccess, onCancel }) => {
  const { user } = useUser();
  const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 
                               process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || 
                               process.env.VITE_STRIPE_PUBLISHABLE_KEY;

  // If Stripe key is not set, show error immediately
  if (!stripePublishableKey) {
    return (
      <div className="subscription-checkout-container">
        <div className="subscription-checkout-error">
          <h3>⚠️ Stripe Not Configured</h3>
          <p>Stripe publishable key is not set. Please add REACT_APP_STRIPE_PUBLISHABLE_KEY to your .env file and restart the dev server.</p>
          {onCancel && (
            <button onClick={onCancel} className="cancel-button" style={{ marginTop: '20px' }}>
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // If we're here, Stripe should be available via Elements provider
  // If not, the inner component will handle the error
  return <SubscriptionCheckoutInner user={user} onSuccess={onSuccess} onCancel={onCancel} />;
};

export default SubscriptionCheckout;

